'use client';

import React, { useState, useEffect } from 'react';
import { 
  Paper, 
  Title, 
  Text, 
  Button, 
  Group, 
  Loader,
  Stack,
  Alert
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';
import { useAuth } from '../hooks/useAuth';
import { getInvitationByToken, acceptInvitation, declineInvitation } from '../services/firestore';
import { Invitation, AcceptInvitationProps } from '../types';

export const AcceptInvitation: React.FC<AcceptInvitationProps> = ({ 
  token,
  onAcceptSuccess,
  onDeclineSuccess,
  onError
}) => {
  const { user } = useAuth();
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  useEffect(() => {
    const loadInvitation = async () => {
      try {
        const inv = await getInvitationByToken(token);
        setInvitation(inv);
        
        if (!inv) {
          setError('Invitation not found or has been processed');
        } else if (inv.status !== 'pending') {
          setError(`This invitation has already been ${inv.status}`);
        } else if (new Date(inv.expiresAt) < new Date()) {
          setError('This invitation has expired');
        }
      } catch (error) {
        console.error('Error loading invitation:', error);
        setError('Failed to load invitation details');
        onError?.(error as Error);
      } finally {
        setLoading(false);
      }
    };
    
    loadInvitation();
  }, [token, onError]);
  
  const handleAccept = async () => {
    if (!user || !invitation) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      await acceptInvitation(token, user.uid);
      setSuccess(`You have joined ${invitation.orgName} as a ${invitation.role}`);
      onAcceptSuccess?.();
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setError((error as Error).message || 'Failed to accept invitation');
      onError?.(error as Error);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleDecline = async () => {
    if (!invitation) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      await declineInvitation(token);
      setSuccess('You have declined the invitation');
      onDeclineSuccess?.();
    } catch (error) {
      console.error('Error declining invitation:', error);
      setError((error as Error).message || 'Failed to decline invitation');
      onError?.(error as Error);
    } finally {
      setProcessing(false);
    }
  };
  
  if (loading) {
    return <Loader />;
  }
  
  if (error) {
    return (
      <Paper radius="md" p="xl" withBorder>
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error}
        </Alert>
      </Paper>
    );
  }
  
  if (success) {
    return (
      <Paper radius="md" p="xl" withBorder>
        <Alert icon={<IconCheck size={16} />} title="Success" color="green">
          {success}
        </Alert>
      </Paper>
    );
  }
  
  if (!invitation) {
    return (
      <Paper radius="md" p="xl" withBorder>
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Invitation not found
        </Alert>
      </Paper>
    );
  }
  
  return (
    <Paper radius="md" p="xl" withBorder>
      <Stack gap="md">
        <Title order={2}>Organization Invitation</Title>
        
        <Text>
          You have been invited to join <strong>{invitation.orgName}</strong> as a <strong>{invitation.role}</strong>.
        </Text>
        
        <Text size="sm">
          Invited by: {invitation.inviterName}
        </Text>
        
        <Text size="sm">
          Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
        </Text>
        
        <Group justify="flex-end" mt="md">
          <Button 
            variant="outline" 
            color="red" 
            leftSection={<IconX size={16} />}
            onClick={handleDecline}
            loading={processing}
          >
            Decline
          </Button>
          
          <Button 
            leftSection={<IconCheck size={16} />}
            onClick={handleAccept}
            loading={processing}
          >
            Accept Invitation
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}; 